import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { doc, setDoc, where } from 'firebase/firestore'
import { useFirestore } from 'reactfire'
import { toast } from 'react-toastify'
import { InputGroup, FormControl, Form } from 'react-bootstrap'
import PageHeader from '../components/PageHeader'
import { AdvancedButton } from '../components/AdvancedButton'
import UsageSection from '../components/UsageSection'
import { useFirebaseDoc, useFirebaseQuery, useFirebaseQueryOptions } from '../hooks/firebaseHooks'
import { User } from '../models/shared/User'
import { Organization } from '../models/shared/Organization'
import { Deal } from '../models/shared/Deal'
import { COLLECTIONS, PLAN_DEAL_LIMITS, PLAN_NAMES, PlanId } from '../utils/shared/constants'
import { formatDate } from '../utils/date'
import { getActiveDealsCount } from '../utils/shared/deal'

export default function SuperAdminUserPage() {
    const { uid } = useParams<{ uid: string }>();
    const firestore = useFirestore();
    const [trialDate, setTrialDate] = useState('');
    const [processing, setProcessing] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState('');

    const [user] = useFirebaseDoc<User>(`${COLLECTIONS.USERS}/${uid}`);
    const [orgs] = useFirebaseQuery<Organization>(
        COLLECTIONS.ORGANIZATIONS,
        where('ownedBy', '==', uid ?? '')
    );

    const org = orgs.find(o => o.id === selectedOrgId) ?? orgs[0] ?? null;
    const planId: PlanId = org?.billing?.planId || 'free';
    const limit = PLAN_DEAL_LIMITS[planId];

    const updateOrganization = async (orgUpdate: Partial<Organization>) => {
        if (!org?.id) return;
        setProcessing(true);
        try {
            await setDoc(doc(firestore, COLLECTIONS.ORGANIZATIONS, org.id), orgUpdate, { merge: true });
            toast.success('Org updated');
        } catch (e) {
            console.error(e);
            toast.error('Failed to update org');
        }
        setProcessing(false);
    };

    const [deals] = useFirebaseQueryOptions<Deal>(
        `${COLLECTIONS.ORGANIZATIONS}/${org?.id ?? ''}/${COLLECTIONS.DEALS}`,
        { queryConstraints: [where('status', '!=', 'archived')], skip: !org }
    );
    const activeDealsCount = getActiveDealsCount(deals);

    return (
        <>
            <PageHeader
                title={user?.email ?? uid ?? ''}
                showBackButton
            />

            <div className='d-flex flex-column gap-3 mt-3' >
                <div className='d-flex flex-column gap-1'>
                    <strong>User</strong>
                    <span>{user?.email}</span>
                    <span className='text-muted' style={{ fontSize: '0.85em' }}>{uid}</span>
                    <span className='text-muted'>Created At: {user ? formatDate(user.createdAt) : '—'}</span>
                    <span className='text-muted'>Last seen: {user ? formatDate(user.lastSeenAt) : '—'}</span>
                </div>

                <hr />

                <div className='d-flex flex-column gap-1'>
                    <strong>Organization</strong>
                    {orgs.length > 1 && (
                        <Form.Select
                            size='sm'
                            style={{ maxWidth: 340 }}
                            value={org?.id ?? ''}
                            onChange={e => setSelectedOrgId(e.target.value)}
                        >
                            {orgs.map(o => (
                                <option key={o.id} value={o.id}>{o.name || o.id}</option>
                            ))}
                        </Form.Select>
                    )}
                    <span>{org?.name ?? '—'}</span>
                    <span>Web: <a href={org?.website ?? '#'}>{org?.website ?? '—'}</a></span>
                    <span>
                        Plan: {PLAN_NAMES[planId]}
                    </span>
                </div>

                {org && (
                    <UsageSection
                        activeDealsCount={activeDealsCount}
                        planDealLimit={limit}
                    />
                )}

                <hr />

                <div className='d-flex flex-column gap-3'>
                    <strong>Actions</strong>
                    <div>

                        <span className='text-muted'>Trial end date {org?.trialEndsAt ? formatDate(org.trialEndsAt) : '—'}</span>
                        <InputGroup style={{ maxWidth: 340 }}>
                            <FormControl
                                type='date'
                                value={trialDate}
                                onChange={e => setTrialDate(e.target.value)}
                            />
                            <AdvancedButton
                                variant='primary'
                                onClick={() => updateOrganization({ trialEndsAt: trialDate ? new Date(trialDate) : null })}
                                processing={processing}
                            >
                                Set trial
                            </AdvancedButton>
                            <AdvancedButton
                                variant='outline-danger'
                                onClick={() => updateOrganization({ trialEndsAt: null })}
                                processing={processing}
                            >
                                Clear trial
                            </AdvancedButton>
                        </InputGroup>
                    </div>

                    {user && (
                        <AdvancedButton
                            variant='primary'
                            icon='bi-eye'
                            href={`/?superUser=${user.email}`}
                        >
                            Switch to super admin view
                        </AdvancedButton>
                    )}
                </div>
            </div>
        </>
    )
}
